import React from 'react';
import { Badge, Card, SectionTitle } from '../../design-system';
import { apiErrorDetails, useCentralLessons } from '../../api/lessonreview';
import { categoryLabel } from './lessonReviewLabels';
import ProposalTable from './ProposalTable';
import styles from './LessonReview.module.css';

/**
 * Livisibly-wide lessons: what ships in the current release (read-only here —
 * central lessons change only through a release) and approvals staged for the
 * next one.
 */
export default function CentralPanel({ enabled = true, onSelectProposal }) {
  const { data, error, isLoading } = useCentralLessons(enabled);
  const deployed = data?.deployed || [];
  const staged = data?.staged || [];

  return (
    <>
      <section className={styles.section}>
        <SectionTitle>Staged for the next release</SectionTitle>
        <p className={styles.intro}>
          Approved Livisibly-wide lessons wait here. They take effect only when they ship in a release —
          approving one never changes anything for anyone on its own.
        </p>
        {error ? (
          <div className={styles.errorBanner} role="alert">
            {apiErrorDetails(error, 'Livisibly-wide lessons could not be loaded.').message}
          </div>
        ) : null}
        {/* No "nothing staged" claim when the list failed to load (review 2026-10-18) */}
        {isLoading ? <p className={styles.emptyText}>Loading…</p> : error ? null : (
          <ProposalTable proposals={staged} onSelect={onSelectProposal}
                         emptyText="Nothing is staged for the next release." />
        )}
      </section>

      <section className={styles.section}>
        <SectionTitle>In the current release</SectionTitle>
        {!isLoading && !error && deployed.length === 0 ? (
          <Card padding="md"><p className={styles.emptyText}>No Livisibly-wide lessons ship yet.</p></Card>
        ) : null}
        {deployed.length > 0 ? (
          <Card padding="none" className={styles.tableCard}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Lesson</th>
                  <th className={styles.th}>Category</th>
                  <th className={styles.th}>Always apply</th>
                  <th className={styles.th}>Version</th>
                </tr>
              </thead>
              <tbody>
                {deployed.map((l) => (
                  <tr key={l.memory_id}>
                    <td className={styles.td}>
                      <div className={styles.titleCell}>{l.title}</div>
                      <p className={styles.rationale}>{l.content}</p>
                    </td>
                    <td className={styles.td}>{categoryLabel(l.category)}</td>
                    <td className={styles.td}>
                      {l.mandatory ? <Badge variant="info" size="sm">Yes</Badge> : 'No'}
                    </td>
                    <td className={styles.td}>{l.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : null}
      </section>
    </>
  );
}
